import React from 'react';
import { Monitor, Tablet, Smartphone, Eye, EyeOff } from 'lucide-react';

interface ResponsiveEditorProps {
  responsive: Record<string, any>;
  onChange: (responsive: Record<string, any>) => void;
}

export const ResponsiveEditor: React.FC<ResponsiveEditorProps> = ({ responsive = {}, onChange }) => {
  const devices = [
    { id: 'desktop', label: 'Desktop', icon: Monitor, minWidth: '1024px' },
    { id: 'tablet', label: 'Tablet', icon: Tablet, minWidth: '768px', maxWidth: '1023px' },
    { id: 'mobile', label: 'Mobile', icon: Smartphone, maxWidth: '767px' }
  ];

  const handleVisibilityChange = (device: string, visible: boolean) => {
    onChange({ [device]: visible });
  };

  return (
    <div className="space-y-6">
      {/* Visibility Controls */}
      <div>
        <h3 className="text-sm font-medium mb-3">Section Visibility</h3>
        <div className="space-y-3">
          {devices.map(device => {
            const Icon = device.icon;
            const isVisible = responsive[device.id] !== false;

            return (
              <div
                key={device.id}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="font-medium">{device.label}</p>
                    <p className="text-xs text-gray-500">
                      {device.minWidth && `≥ ${device.minWidth}`}
                      {device.maxWidth && device.minWidth && ' - '}
                      {device.maxWidth && `≤ ${device.maxWidth}`}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => handleVisibilityChange(device.id, !isVisible)}
                  className={`p-2 rounded-lg transition-colors ${
                    isVisible ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'
                  }`}
                  title={isVisible ? 'Visible' : 'Hidden'}
                >
                  {isVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Responsive Spacing */}
      <div>
        <h3 className="text-sm font-medium mb-3">Responsive Spacing</h3>
        <div className="space-y-4">
          {devices.map(device => {
            const Icon = device.icon;
            const deviceKey = `${device.id}Spacing`;
            const spacing = responsive[deviceKey] || {};

            return (
              <div key={device.id} className="border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-3">
                  <Icon className="w-4 h-4 text-gray-600" />
                  <span className="text-sm font-medium">{device.label} Spacing</span>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-600">Padding</label>
                    <select
                      value={spacing.padding || 'default'}
                      onChange={(e) => onChange({
                        [deviceKey]: { ...spacing, padding: e.target.value }
                      })}
                      className="w-full px-2 py-1 border rounded text-sm"
                    >
                      <option value="default">Default</option>
                      <option value="0">None</option>
                      <option value="4px">Tight (4px)</option>
                      <option value="8px">Small (8px)</option>
                      <option value="16px">Medium (16px)</option>
                      <option value="24px">Large (24px)</option>
                      <option value="32px">Extra Large (32px)</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-xs text-gray-600">Margin</label>
                    <select
                      value={spacing.margin || 'default'}
                      onChange={(e) => onChange({
                        [deviceKey]: { ...spacing, margin: e.target.value }
                      })}
                      className="w-full px-2 py-1 border rounded text-sm"
                    >
                      <option value="default">Default</option>
                      <option value="0">None</option>
                      <option value="4px">Tight (4px)</option>
                      <option value="8px">Small (8px)</option>
                      <option value="16px">Medium (16px)</option>
                      <option value="24px">Large (24px)</option>
                      <option value="32px">Extra Large (32px)</option>
                    </select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Responsive Typography */}
      <div>
        <h3 className="text-sm font-medium mb-3">Responsive Typography</h3>
        <div className="space-y-4">
          {devices.map(device => {
            const Icon = device.icon;
            const deviceKey = `${device.id}Typography`;
            const typography = responsive[deviceKey] || {};

            return (
              <div key={device.id} className="border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-3">
                  <Icon className="w-4 h-4 text-gray-600" />
                  <span className="text-sm font-medium">{device.label} Typography</span>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-600">Font Size</label>
                    <select
                      value={typography.fontSize || 'default'}
                      onChange={(e) => onChange({
                        [deviceKey]: { ...typography, fontSize: e.target.value }
                      })}
                      className="w-full px-2 py-1 border rounded text-sm"
                    >
                      <option value="default">Default</option>
                      <option value="12px">12px</option>
                      <option value="14px">14px</option>
                      <option value="16px">16px</option>
                      <option value="18px">18px</option>
                      <option value="20px">20px</option>
                      <option value="24px">24px</option>
                      <option value="32px">32px</option>
                      <option value="48px">48px</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-xs text-gray-600">Text Align</label>
                    <select
                      value={typography.textAlign || 'default'}
                      onChange={(e) => onChange({
                        [deviceKey]: { ...typography, textAlign: e.target.value }
                      })}
                      className="w-full px-2 py-1 border rounded text-sm"
                    >
                      <option value="default">Default</option>
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                      <option value="justify">Justify</option>
                    </select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Layout Direction */}
      <div>
        <h3 className="text-sm font-medium mb-3">Layout Direction</h3>
        <div className="space-y-4">
          {devices.map(device => {
            const Icon = device.icon;
            const deviceKey = `${device.id}Layout`;
            const layout = responsive[deviceKey] || {};

            return (
              <div key={device.id} className="border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-3">
                  <Icon className="w-4 h-4 text-gray-600" />
                  <span className="text-sm font-medium">{device.label} Layout</span>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-600">Direction</label>
                    <select
                      value={layout.flexDirection || 'default'}
                      onChange={(e) => onChange({
                        [deviceKey]: { ...layout, flexDirection: e.target.value }
                      })}
                      className="w-full px-2 py-1 border rounded text-sm"
                    >
                      <option value="default">Default</option>
                      <option value="row">Row</option>
                      <option value="row-reverse">Row Reverse</option>
                      <option value="column">Column</option>
                      <option value="column-reverse">Column Reverse</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-xs text-gray-600">Columns</label>
                    <select
                      value={layout.columns || 'default'}
                      onChange={(e) => onChange({
                        [deviceKey]: { ...layout, columns: e.target.value }
                      })}
                      className="w-full px-2 py-1 border rounded text-sm"
                    >
                      <option value="default">Default</option>
                      <option value="1">1 Column</option>
                      <option value="2">2 Columns</option>
                      <option value="3">3 Columns</option>
                      <option value="4">4 Columns</option>
                    </select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Breakpoint Preview */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-sm font-medium mb-2">Preview Breakpoints</h3>
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Mobile</span>
            <span className="font-mono text-xs bg-white px-2 py-1 rounded">320px - 767px</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Tablet</span>
            <span className="font-mono text-xs bg-white px-2 py-1 rounded">768px - 1023px</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Desktop</span>
            <span className="font-mono text-xs bg-white px-2 py-1 rounded">1024px+</span>
          </div>
        </div>
      </div>
    </div>
  );
};